import { ArrowLeftRight } from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  FormModal, FormField, FormInput, FormSelect 
} from '@/components/ui/form-modal';
import type { StockItem } from '@/features/inventory/types';
import { VENUE_OPTIONS } from './inventory-constants';

interface TransferFormData {
  fromVenue: string;
  toVenue: string;
  itemId: string;
  quantity: string;
}

interface TransfersViewProps {
  readonly items: StockItem[];
  readonly showTransferModal: boolean;
  readonly setShowTransferModal: (show: boolean) => void;
  readonly transferFormData: TransferFormData;
  readonly setTransferFormData: (data: TransferFormData) => void;
  readonly onCreateTransfer: () => void;
  readonly onSaveTransfer: () => void;
}

export function TransfersView({
  items,
  showTransferModal,
  setShowTransferModal,
  transferFormData,
  setTransferFormData,
  onCreateTransfer,
  onSaveTransfer,
}: TransfersViewProps) {
  const itemOptions = items.map((i) => ({
    value: i.itemId,
    label: i.itemName 
  }));

  return (
    <>
      <CrudToolbar
        entityName="Transfer"
        entityNamePlural="Transfers"
        onAdd={onCreateTransfer}
        canEdit={false}
        canDelete={false}
        canBulkUpload={false}
        canExport={false}
      />

      <FormModal
        title="Create Transfer"
        description="Transfer stock between venues"
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onSave={onSaveTransfer}
        saveLabel="Create Transfer"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="From Venue" required>
            <FormSelect
              value={transferFormData.fromVenue}
              onChange={(e) => setTransferFormData({
                ...transferFormData,
                fromVenue: e.target.value 
              })}
              placeholder="Select source..."
              options={VENUE_OPTIONS}
            />
          </FormField>
          <FormField label="To Venue" required>
            <FormSelect
              value={transferFormData.toVenue}
              onChange={(e) => setTransferFormData({
                ...transferFormData,
                toVenue: e.target.value 
              })}
              placeholder="Select destination..."
              options={VENUE_OPTIONS}
            />
          </FormField>
          <FormField label="Item" required>
            <FormSelect
              value={transferFormData.itemId}
              onChange={(e) => setTransferFormData({
                ...transferFormData,
                itemId: e.target.value 
              })}
              placeholder="Select item..."
              options={itemOptions}
            />
          </FormField>
          <FormField label="Quantity" required>
            <FormInput
              type="number"
              min="1"
              value={transferFormData.quantity}
              onChange={(e) => setTransferFormData({
                ...transferFormData,
                quantity: e.target.value 
              })}
              placeholder="Enter quantity"
            />
          </FormField>
        </div>
      </FormModal>

      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <ArrowLeftRight className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">Inventory Transfers</p>
          <p className="text-sm text-muted-foreground">Transfer stock between venues and storage locations</p>
        </CardContent>
      </Card>
    </>
  );
}
